<template>
    <div class="mt-3">
        <div class="flex flex-col gap-3 md:grid md:grid-cols-3 md:items-center">
            <div class="hidden md:block"></div>
            <div class="flex flex-col items-center gap-2">
                <h1 class="text-3xl text-center">
                    Banners de:
                    <strong class="text-theme-primary">
                        {{ sectionName }}
                    </strong>
                    <small class="text-base">({{ itemsNumber }})</small>
                </h1>
                <p v-if="relevanceMode" class="text-sm text-theme-muted">
                    Ordenando solo los banners activos de estos filtros.
                </p>
            </div>
            <div class="flex justify-center md:justify-end">
                <app-button
                    v-if="
                        $ability.hasPermission('banner:Create') &&
                        !relevanceMode
                    "
                    color="success"
                    size="large"
                    @click="$router.push('/app/banners/new')"
                >
                    <span class="hidden md:inline">Nuevo banner</span>
                    <span class="md:hidden font-bold">+</span>
                </app-button>
            </div>
        </div>

        <div class="flex flex-wrap mt-2">
            <div class="w-full my-4">
                <banners-filters
                    v-model="query"
                    :enabled-filters="$data.$enabledFilters"
                    @enter="reloadItems"
                />
                <div class="flex justify-center mt-2">
                    <app-button v-show="query" relief @click="reloadItems">
                        Consultar 🔎
                    </app-button>
                </div>
            </div>

            <div
                class="flex flex-col lg:flex-row items-center justify-between w-full mt-3 gap-4"
            >
                <div
                    class="flex flex-wrap items-center justify-center lg:justify-start gap-2"
                >
                    <tooltip-button
                        v-show="
                            selected.length &&
                            $ability.hasPermission('banner:Create') &&
                            !relevanceMode
                        "
                        icon="🗂️"
                        message="Clonar banner"
                        @click="cloneSelected"
                    />
                </div>

                <div
                    class="flex flex-wrap items-center justify-center lg:justify-end gap-3"
                >
                    <app-button
                        v-if="relevanceMode"
                        color="success"
                        @click="saveRelevance"
                    >
                        Guardar orden 💾
                    </app-button>

                    <app-button
                        v-if="$ability.hasPermission('banner:Update')"
                        :color="relevanceMode ? 'warn' : 'primary'"
                        @click="toggleRelevanceMode"
                    >
                        {{ relevanceMode ? "⚖️ Salir" : "⚖️ Editar orden" }}
                    </app-button>

                    <list-settings-button
                        v-if="!relevanceMode"
                        :value="{ pageSize }"
                        @change="onListSettingsChange"
                    />
                    <custom-pagination
                        v-model="currentPage"
                        :length="pagesNumber"
                        @input="listItems()"
                    />
                </div>
            </div>
        </div>

        <div class="w-full mt-2">
            <custom-sortable-table
                ref="selectableTable"
                :fields="activeFields"
                :items="itemsCurrent"
                :per-page="itemsCurrent.length"
                :selectable="!relevanceMode"
                :draggable="relevanceMode"
                :external-current-page="currentPage"
                :external-total-pages="pagesNumber"
                :row-actions="rowActions"
                select-mode="single"
                responsive
                no-border-collapse
                hover
                class="w-full"
                :items-empty-message="itemsEmptyMessage"
                @flex-selected="onRowSelected"
                @drag-drop="onDragDrop"
                @row-send-to-page="onRowSendToPage"
                @row-action="onRowAction"
            >
                <template #cell(id)="data">
                    <router-link
                        :to="`/app/banners/detail/${data.item.id}`"
                        class="text-center btn btn-stack-primary p-1"
                    >
                        <small class="ml-1">
                            {{ data.item.active ? "🟢" : "⚪" }}
                        </small>
                        <small>
                            {{ data.item.id }}
                        </small>
                    </router-link>
                </template>

                <template #cell(image)="data">
                    <router-link :to="`/app/banners/detail/${data.item.id}`">
                        <img
                            v-img-loading
                            :src="
                                (data.item.image
                                    ? data.item.image.url
                                    : undefined) | image
                            "
                            :alt="data.item.image ? data.item.image.alt : ''"
                            class="w-24 h-12 object-cover rounded"
                        />
                    </router-link>
                </template>

                <template #cell(name)="data">
                    <span class="text-center">
                        {{ data.item.name | cut(36) }}
                    </span>
                </template>

                <template #cell(sequence)="data">
                    <span class="text-center">
                        {{ data.item.sequence }}
                    </span>
                </template>

                <template #cell(updatedAt)="data">
                    <small class="text-center">
                        {{ data.item.updatedAt | date }}
                    </small>
                </template>
            </custom-sortable-table>
        </div>
    </div>
</template>

<script>
import urlQuerySyncMixin from "@/mixins/urlQuerySyncMixin";
import { mapActions, mapGetters } from "vuex";

import {
    REORDER_PAGE_SIZE,
    ROOT_LINE_NAME,
    ROOT_LINE_SLUG
} from "@/common/constants";
import { buildQueryFromUrl } from "@/common/lib/url-query-filters";

import BannersFilters from "@/components/banners/BannersFilters.vue";
import CustomSortableTable from "@/components/utils/CustomSortableTable.vue";
import TooltipButton from "@/components/utils/TooltipButton.vue";

export default {
    name: "BannersIndex",
    components: {
        BannersFilters,
        CustomSortableTable,
        TooltipButton
    },
    mixins: [urlQuerySyncMixin],
    layout: "app",
    data: () => ({
        // La sección (Página de inicio o una categoría) es un filtro más,
        // puesto por defecto en la Página de inicio; se puede cambiar o quitar.
        query: { lineSlug: ROOT_LINE_SLUG },
        lineNames: {},
        itemsCurrent: [],
        $fields: [
            { key: "id", label: "id", sortable: false },
            { key: "image", label: "Imagen", sortable: false },
            { key: "name", label: "Nombre", sortable: true },
            { key: "sequence", label: "Orden", sortable: true },
            { key: "updatedAt", label: "📆 Actualización", sortable: true }
        ],
        $enabledFilters: ["line", "keyword"],
        $urlPaginationKey: "page",
        $urlFilterSchema: [
            { key: "keyword", type: "string" },
            { key: "lineSlug", type: "string" }
        ],

        currentPage: 1,
        pagesNumber: 0,
        itemsNumber: 0,
        pageSize: 24,
        relevanceMode: false,
        selected: []
    }),
    computed: {
        ...mapGetters("control", ["backgroundColor"]),
        listPageSize() {
            return this.relevanceMode ? REORDER_PAGE_SIZE : this.pageSize;
        },
        sectionName() {
            const { lineSlug } = this.query;
            if (!lineSlug) return "todas las secciones";
            if (lineSlug === ROOT_LINE_SLUG) return ROOT_LINE_NAME;
            return this.lineNames[lineSlug] || lineSlug;
        },
        itemsEmptyMessage() {
            return this.relevanceMode
                ? "No hay banners activos con estos filtros para ordenar."
                : "No hay banners con estos filtros. Cambia los filtros o crea uno nuevo.";
        },
        // En modo ordenar solo entran los activos: los apagados no se ven
        // en el catálogo y no tiene sentido ubicarlos.
        listFilters() {
            return this.relevanceMode
                ? { ...this.query, active: true }
                : this.query;
        },
        rowActions() {
            const actions = [
                { key: "open", icon: "🔗", label: "Abrir en nueva pestaña" }
            ];
            if (this.$ability.hasPermission("banner:Create")) {
                actions.push({
                    key: "clone",
                    icon: "🗂️",
                    label: "Clonar banner"
                });
            }
            return actions;
        },
        activeFields() {
            if (this.relevanceMode) {
                return this.$data.$fields.map((field) => ({
                    ...field,
                    sortable: false
                }));
            }
            return this.$data.$fields;
        }
    },
    watch: {
        "$route.fullPath"(newPath, oldPath) {
            if (newPath === oldPath) return;
            this.query = {
                lineSlug: ROOT_LINE_SLUG,
                ...buildQueryFromUrl(
                    this.$route.query,
                    this.$data.$urlFilterSchema
                )
            };
            this.reloadItems();
        }
    },
    async mounted() {
        const verifyPagePermission =
            this.$ability.verifyPagePermission.bind(this);
        await verifyPagePermission("banner:List", "/app");
        this.listItems(true);
    },
    methods: {
        ...mapActions("banners", ["listBanners", "updateBannersWeights"]),
        async listItems(complete = false) {
            this.isLoading = true;
            this.$loading.show();
            try {
                const { items, pagination } = await this.listBanners({
                    complete,
                    itemsByPage: this.listPageSize,
                    page: this.currentPage,
                    ...this.listFilters
                });
                this.itemsCurrent = items;
                if (pagination) {
                    this.itemsNumber = pagination.itemsNumber;
                    this.pagesNumber = pagination.pagesNumber;
                }
                this.syncSectionName(items);
                this.$loading.hide();
            } catch (error) {
                this.$loading.hide();
                this.isLoading = false;
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        // Del filtro solo se conoce el slug; el nombre de la categoría llega
        // con los banners que trae el listado.
        syncSectionName(items) {
            const names = { ...this.lineNames };
            items.forEach((item) => {
                if (item.line && item.line.slug) {
                    names[item.line.slug] = item.line.name;
                }
            });
            this.lineNames = names;
        },
        async reloadItems() {
            this.currentPage = 1;
            this.clearSelected();
            await this.listItems(true);
        },
        onListSettingsChange({ pageSize }) {
            this.pageSize = pageSize;
            this.reloadItems();
        },
        onRowSelected(items) {
            this.selected = items;
        },
        onRowAction({ action, item }) {
            switch (action) {
                case "open":
                    window.open(`/app/banners/detail/${item.id}`, "_blank");
                    break;
                case "clone":
                    window.open(`/app/banners/clone/${item.id}`, "_blank");
                    break;
            }
        },
        cloneSelected() {
            window
                .open(`/app/banners/clone/${this.selected[0].id}`, "_blank")
                .focus();
        },
        clearSelected() {
            if (this.$refs.selectableTable) {
                this.$refs.selectableTable.clearSelected();
            }
            this.selected = [];
        },
        toggleRelevanceMode() {
            this.relevanceMode = !this.relevanceMode;
            if (this.relevanceMode) this.clearSelected();
            this.currentPage = 1;
            this.listItems(true);
        },
        onDragDrop(reorderedItems) {
            const offset = (this.currentPage - 1) * this.listPageSize;
            this.itemsCurrent = reorderedItems.map((item, index) => ({
                ...item,
                sequence: offset + index + 1
            }));
        },
        async onRowSendToPage({ item, page }) {
            if (page === this.currentPage) return;
            const targetSequence = page * this.listPageSize - 1;
            this.itemsCurrent = this.itemsCurrent.filter(
                (banner) => banner.id !== item.id
            );
            this.$loading.show();
            try {
                await this.updateBannersWeights({
                    banners: [{ id: item.id, sequence: targetSequence }]
                });
                this.$sound.play();
                this.$loading.hide();
            } catch (error) {
                this.$loading.hide();
                this.itemsCurrent = [...this.itemsCurrent, item];
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async saveRelevance() {
            this.$loading.show();
            const offset = (this.currentPage - 1) * this.listPageSize;
            try {
                await this.updateBannersWeights({
                    banners: this.itemsCurrent.map((item, index) => ({
                        id: item.id,
                        sequence: offset + index + 1
                    }))
                });
                this.$sound.play();
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Orden actualizado",
                    icon: "success",
                    showCloseButton: true,
                    timer: 3000
                });
                this.$loading.hide();
            } catch (error) {
                this.$loading.hide();
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        }
    }
};
</script>
